import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Search,
    CheckCircle,
    Clock,
    XCircle,
    RefreshCw,
    TrendingUp,
    TrendingDown,
    Zap
} from 'lucide-react';

const AdminFuturesTradesPage = () => {
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sideFilter, setSideFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

    useEffect(() => {
        fetchPositions();
    }, [statusFilter, sideFilter]);

    const fetchPositions = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (sideFilter) params.append('side', sideFilter);
            params.append('limit', pagination.limit);
            params.append('offset', pagination.offset);

            const res = await api.get(`/admin/futures-positions?${params.toString()}`);
            setPositions(res.data.positions || []);
            setPagination(res.data.pagination || pagination);
        } catch (error) {
            console.error('Fetch futures positions error:', error);
            toast.error('Failed to load futures positions');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            OPEN: 'bg-green-500/20 text-green-400 border-green-500/30',
            CLOSED: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
            LIQUIDATED: 'bg-red-500/20 text-red-400 border-red-500/30'
        };
        const icons = {
            OPEN: <CheckCircle size={14} />,
            CLOSED: <XCircle size={14} />,
            LIQUIDATED: <XCircle size={14} />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.OPEN}`}>
                {icons[status] || icons.OPEN}
                {status}
            </span>
        );
    };

    const filteredPositions = positions.filter(p => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            p.user?.email?.toLowerCase().includes(search) ||
            p.symbol?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <Zap className="text-yellow-400" /> Futures Positions
                        </h1>
                        <p className="text-gray-400 text-sm">View all user futures trading positions</p>
                    </div>
                    <button
                        onClick={fetchPositions}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex flex-wrap gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email or symbol..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="OPEN">Open</option>
                        <option value="CLOSED">Closed</option>
                        <option value="LIQUIDATED">Liquidated</option>
                    </select>
                    <select
                        value={sideFilter}
                        onChange={(e) => setSideFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Sides</option>
                        <option value="LONG">Long</option>
                        <option value="SHORT">Short</option>
                    </select>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading futures positions...</div>
                    ) : filteredPositions.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                            <Zap size={48} className="mx-auto mb-4 text-yellow-400/50" />
                            <p className="text-lg font-medium">No futures positions found</p>
                            <p className="text-sm mt-2">Futures positions will appear here when users trade</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Symbol</th>
                                        <th className="px-4 py-3 font-medium">Side</th>
                                        <th className="px-4 py-3 font-medium">Leverage</th>
                                        <th className="px-4 py-3 font-medium">Size</th>
                                        <th className="px-4 py-3 font-medium">Entry Price</th>
                                        <th className="px-4 py-3 font-medium">Exit Price</th>
                                        <th className="px-4 py-3 font-medium">PnL</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredPositions.map((position) => {
                                        const isClosed = position.status === 'CLOSED' || position.status === 'LIQUIDATED';
                                        const displaySize = isClosed ? (position.closedAmount || position.amount) : position.amount;
                                        const displayPnl = isClosed ? (position.realizedPnl || 0) : (position.unrealizedPnl || 0);
                                        const displayMargin = isClosed ? (position.closedAmount ? parseFloat(position.entryPrice) * parseFloat(position.closedAmount) / position.leverage : 0) : position.margin;

                                        return (
                                            <tr key={position.id} className="hover:bg-slate-700/30 transition">
                                                <td className="px-4 py-3">
                                                    <div>
                                                        <div className="text-white font-medium text-sm">{position.user?.email || 'Unknown'}</div>
                                                        <div className="text-xs text-gray-400">
                                                            {position.user?.firstName} {position.user?.lastName}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3 text-white font-medium">{position.symbol}</td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${position.side === 'LONG'
                                                        ? 'bg-green-500/20 text-green-400'
                                                        : 'bg-red-500/20 text-red-400'
                                                        }`}>
                                                        {position.side === 'LONG' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                                        {position.side}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-yellow-400 font-medium">{position.leverage}x</td>
                                                <td className="px-4 py-3 text-white font-mono text-sm">
                                                    {parseFloat(displaySize).toFixed(4)}
                                                </td>
                                                <td className="px-4 py-3 text-white font-mono text-sm">
                                                    ${parseFloat(position.entryPrice).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3 text-gray-300 font-mono text-sm">
                                                    {isClosed && position.exitPrice ? `$${parseFloat(position.exitPrice).toFixed(2)}` : '-'}
                                                </td>
                                                <td className={`px-4 py-3 font-mono text-sm ${parseFloat(displayPnl) >= 0 ? 'text-green-400' : 'text-red-400'
                                                    }`}>
                                                    {parseFloat(displayPnl) >= 0 ? '+' : ''}${parseFloat(displayPnl).toFixed(2)}
                                                </td>
                                                <td className="px-4 py-3">{getStatusBadge(position.status)}</td>
                                                <td className="px-4 py-3 text-gray-400 text-sm">
                                                    {new Date(position.createdAt).toLocaleString()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                <div className="mt-4 text-center text-gray-400 text-sm">
                    Showing {filteredPositions.length} of {pagination.total} positions
                </div>
            </div>
        </div>
    );
};

export default AdminFuturesTradesPage;
